import React, { useMemo } from 'react';
import { Card } from 'tea-component';
import classnames from 'classnames';
import moment from 'moment';
import 'moment/locale/zh-cn';
import { useIntl } from 'umi';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { getLanguage } from '@home/utils/common';
import { LANGUAGE_EN } from '@home/_config/config';
import { dynamicString } from '@home/utils/dynamicString';
import { resolveStudioKpiTitleIconSrc } from '@home/pages/studio/home/_constant';
import { getTechlandTrendTitleDateStr } from '@home/pages/studio/home/_trendUtils';
import type {
  ITechlandYtdMetricRow,
  ITechlandYtdRevenueCardProps,
  ITechlandYtdVarianceRow,
  TTechlandDateTypeKey,
} from './types';
import './index.scss';

type TMetricGroup = ITechlandYtdRevenueCardProps['budgetGroup'];

/** 单列内一行指标槽位：pageConfig 里 ytd / completion / vsVariance 各自可配 showSignPrefix、useToneColor */
type TMetricRowSlot = 'ytd' | 'completion' | 'vsVariance';

function resolveRowShowSignPrefix(
  row: ITechlandYtdMetricRow,
  slot: TMetricRowSlot,
  cardVsDefault: boolean | undefined,
): boolean {
  if (row.showSignPrefix !== undefined) {
    return row.showSignPrefix;
  }
  if (slot === 'vsVariance') {
    return cardVsDefault ?? true;
  }
  return false;
}

function resolveRowUseToneColor(
  row: ITechlandYtdMetricRow,
  slot: TMetricRowSlot,
  cardVsDefault: boolean | undefined,
): boolean {
  if (row.useToneColor !== undefined) {
    return row.useToneColor;
  }
  if (slot === 'vsVariance') {
    return cardVsDefault ?? true;
  }
  return false;
}

const DEFAULT_ACTUAL_FORMAT = 'bmk00';

const SUBTITLE_DATE_PLACEHOLDER = '{{dateStr}}';
const DEFAULT_SUBTITLE_TEMPLATE_EN = `As of ${SUBTITLE_DATE_PLACEHOLDER}`;
const DEFAULT_SUBTITLE_TEMPLATE_CN = `截至 ${SUBTITLE_DATE_PLACEHOLDER}`;

/** subtitleDate → 模板中的 dateStr；formatPattern 为 moment 格式串 */
function formatSubtitleDateStr(
  raw: string,
  formatPattern: string | undefined,
  isEn: boolean,
): string {
  const trimmed = String(raw).trim();
  if (!trimmed) {
    return '';
  }
  if (!formatPattern) {
    return trimmed;
  }
  const strict = moment(
    trimmed,
    ['YYYY-MM-DD', 'YYYY/MM/DD', 'YYYY-MM-DD HH:mm:ss'],
    true,
  );
  const parsed = strict.isValid() ? strict : moment(trimmed);
  if (!parsed.isValid()) {
    return trimmed;
  }
  const locale = isEn ? 'en' : 'zh-cn';
  return parsed.clone().locale(locale).format(formatPattern);
}

function buildSubtitleText(
  isEn: boolean,
  subtitleDate: string | null | undefined,
  subTitle: string | undefined,
  subTitleCn: string | undefined,
  subTitleDateFormat: string | undefined,
  subTitleDateFormatCn: string | undefined,
): string | null {
  const trimmedDate =
    subtitleDate !== undefined && subtitleDate !== null
      ? String(subtitleDate).trim()
      : '';
  const dynamicCtx = { _cardItemTime: trimmedDate };
  const fmt = isEn ? subTitleDateFormat : (subTitleDateFormatCn ?? subTitleDateFormat);
  const dateStr = trimmedDate
    ? formatSubtitleDateStr(trimmedDate, fmt, isEn)
    : '';

  const fromConfig = getLanguage(isEn, subTitle, subTitleCn);
  const hasConfig =
    fromConfig !== undefined &&
    fromConfig !== null &&
    String(fromConfig).trim() !== '';
  let template: string;
  if (hasConfig) {
    template = String(fromConfig).trim();
  } else if (isEn) {
    template = DEFAULT_SUBTITLE_TEMPLATE_EN;
  } else {
    template = DEFAULT_SUBTITLE_TEMPLATE_CN;
  }

  // 统一支持 `{{_cardItemTime|dateTimeFormat;...}}` 写法（与 pageConfig endEnName/endCnName 一致）
  if (template.includes('{{') && template.includes('|')) {
    const out = dynamicString(template, dynamicCtx, template);
    return out ? String(out) : null;
  }

  if (!template.includes(SUBTITLE_DATE_PLACEHOLDER)) {
    return template || null;
  }
  if (!dateStr) {
    return null;
  }
  return template.replace(/\{\{dateStr\}\}/g, dateStr);
}

/** 展示用：根据数值符号决定涨跌色（Service 已算好 tone 时优先用 props） */
const parseNumericSign = (value: number | string | null | undefined): number => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  if (typeof value === 'number') {
    if (value === 0) {
      return 0;
    }
    return value > 0 ? 1 : -1;
  }
  const cleaned = String(value).replace(/[^0-9.+-]/g, '');
  const n = parseFloat(cleaned);
  if (Number.isNaN(n)) {
    return 0;
  }
  if (n === 0) {
    return 0;
  }
  return n > 0 ? 1 : -1;
};

/**
 * 仅用于 Vs. Budget / Vs. Plan：正数 +$x，负数负号在货币符号前（-$x）。
 * 无 preValue 时不加 +。
 */
function formatVsVarianceSignedCurrency(
  rawValue: number | string | null | undefined,
  preValue: string | undefined,
  formattedText: string,
): string {
  if (!preValue) {
    return formattedText;
  }
  const sign = parseNumericSign(rawValue);
  const neg = /^(-|\u2212)/.exec(formattedText);
  if (neg) {
    const rest = formattedText.slice(neg[0].length);
    return `-${preValue}${rest}`;
  }
  const rest = formattedText.replace(/^\+\s*/, '').trim();
  if (sign > 0) {
    return `+${preValue}${rest}`;
  }
  return `${preValue}${rest}`;
}

const resolveVarianceTone = (
  row: ITechlandYtdVarianceRow,
  useToneColor: boolean,
): 'positive' | 'negative' | 'neutral' => {
  if (!useToneColor) {
    return 'neutral';
  }
  if (row.tone) {
    return row.tone;
  }
  const sign = parseNumericSign(row.value);
  if (sign > 0) {
    return 'positive';
  }
  if (sign < 0) {
    return 'negative';
  }
  return 'neutral';
};

/**
 * Budget/Plan Completion%：值为比例（1 = 100%）。未达 100% 红，超过 100% 绿，恰 100% 默认色。
 */
const resolveCompletionPercentTone = (
  raw: number | string | null | undefined,
): 'positive' | 'negative' | 'neutral' => {
  if (raw === null || raw === undefined || raw === '') {
    return 'neutral';
  }
  const n =
    typeof raw === 'number'
      ? raw
      : parseFloat(String(raw).replace(/[^0-9.+-]/g, ''));
  if (!Number.isFinite(n)) {
    return 'neutral';
  }
  const eps = 1e-9;
  if (n < 1 - eps) {
    return 'negative';
  }
  if (n > 1 + eps) {
    return 'positive';
  }
  return 'neutral';
};

/** 展示格式化：按 formatRule / preValue 输出字符串 */
const formatRowValue = (row: ITechlandYtdMetricRow): string => {
  if (row.value === null || row.value === undefined || row.value === '') {
    return '-';
  }
  const formatted = dataFormat({
    value: row.value,
    formatRule: row.formatRule,
  });
  const text = String(formatted);
  if (row.preValue) {
    return `${row.preValue}${text}`;
  }
  return text;
};

/** Vs. 行：showSignPrefix 为 true 时正数 +$、负数 -$；为 false 时与普通金额行一致 */
const formatVarianceRowValue = (
  row: ITechlandYtdVarianceRow,
  showSignPrefix: boolean,
): string => {
  if (row.value === null || row.value === undefined || row.value === '') {
    return '-';
  }
  const formatted = dataFormat({
    value: row.value,
    formatRule: row.formatRule,
  });
  const text = String(formatted);
  if (!showSignPrefix) {
    if (row.preValue) {
      return `${row.preValue}${text}`;
    }
    return text;
  }
  return formatVsVarianceSignedCurrency(row.value, row.preValue, text);
};

/**
 * Techland YTD Revenue 纯展示卡片：仅依赖 ITechlandYtdRevenueCardProps；
 * 与接口/合并逻辑请用 TechlandYtdRevenueCardSlot + TechlandYtdCardMetricsService。
 */
const TechlandYtdRevenueCard: React.FC<ITechlandYtdRevenueCardProps> = props => {
  const {
    className,
    style,
    filterVal,
    titleEn,
    titleCn,
    titleByDateType,
    titleByScope,
    iconSrc,
    titleIconSrcKey,
    subtitleDate,
    subTitle,
    subTitleCn,
    subTitleDateFormat,
    subTitleDateFormatCn,
    actualLabelEn,
    actualLabelCn,
    actualLabelByDateType,
    actualValue,
    actualFormatRule = DEFAULT_ACTUAL_FORMAT,
    actualPreValue,
    actualValueStyle,
    budgetGroup,
    planGroup,
    vsVarianceShowSignPrefix,
    vsVarianceUseToneColor,
  } = props || {};

  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  // const { tipEn, tipCn } = useStudioExplain(
  //   rainbowKey ?? '',
  //   explainEn,
  //   explainCn,
  // );

  const titleIconResolved = useMemo(
    () =>
      resolveStudioKpiTitleIconSrc({
        titleIconSrc: iconSrc,
        titleIconSrcKey,
      }),
    [iconSrc, titleIconSrcKey],
  );

  const actualDisplay = useMemo(() => {
    if (actualValue === null || actualValue === undefined || actualValue === '') {
      return '-';
    }
    const formatted = dataFormat({
      value: actualValue,
      formatRule: actualFormatRule,
    });
    const text = String(formatted);
    return actualPreValue ? `${actualPreValue}${text}` : text;
  }, [actualValue, actualFormatRule, actualPreValue]);

  const subtitleText = useMemo(
    () =>
      buildSubtitleText(
        isEn,
        subtitleDate,
        subTitle,
        subTitleCn,
        subTitleDateFormat,
        subTitleDateFormatCn,
      ),
    [
      isEn,
      subtitleDate,
      subTitle,
      subTitleCn,
      subTitleDateFormat,
      subTitleDateFormatCn,
    ],
  );

  const rawDt = (filterVal as { date_type?: string; } | undefined)?.date_type;
  const titleDateTypeKey = (rawDt === 'weekly' || rawDt === 'monthly'
    ? rawDt
    : 'daily') as TTechlandDateTypeKey;
  let titleDateType: 'weekly' | 'monthly' | undefined;
  if (rawDt === 'weekly') {
    titleDateType = 'weekly';
  } else if (rawDt === 'monthly') {
    titleDateType = 'monthly';
  } else {
    titleDateType = undefined;
  }
  const dateStrToken = getTechlandTrendTitleDateStr(titleDateType);
  const titleCtx = useMemo(() => ({ dateStr: dateStrToken }), [dateStrToken]);

  const scope = (filterVal as { scope?: string; } | undefined)?.scope;
  const scopeConfig = scope ? titleByScope?.[scope] : undefined;
  const mappedFromScope = scopeConfig?.titleByDateType?.[titleDateTypeKey];
  const mappedFromCard = titleByDateType?.[titleDateTypeKey];
  const mapped = mappedFromScope ?? mappedFromCard;
  const titleEnTemplate =
    mapped?.en ||
    scopeConfig?.titleEn ||
    titleEn;
  const titleCnTemplate =
    mapped?.cn ||
    scopeConfig?.titleCn ||
    titleCn;

  const actualLabelResolved = useMemo(() => {
    const mappedActual = actualLabelByDateType?.[titleDateTypeKey];
    if (mappedActual?.en) {
      return getLanguage(isEn, mappedActual.en, mappedActual.cn ?? mappedActual.en);
    }
    return getLanguage(isEn, actualLabelEn, actualLabelCn);
  }, [actualLabelByDateType, actualLabelEn, actualLabelCn, isEn, titleDateTypeKey]);

  const displayTitleEn = useMemo(() => {
    if (titleEnTemplate?.includes('{{')) {
      return dynamicString(titleEnTemplate, titleCtx, titleEnTemplate);
    }
    return titleEnTemplate;
  }, [titleEnTemplate, titleCtx]);

  const displayTitleCn = useMemo(() => {
    if (titleCnTemplate?.includes('{{')) {
      return dynamicString(titleCnTemplate, titleCtx, titleCnTemplate);
    }
    return titleCnTemplate;
  }, [titleCnTemplate, titleCtx]);

  if (!budgetGroup || !planGroup || !titleEn || !titleCn) {
    return null;
  }

  const renderMetricRow = (
    row: ITechlandYtdMetricRow,
    slot: TMetricRowSlot,
  ): React.ReactNode => {
    const showSign = resolveRowShowSignPrefix(
      row,
      slot,
      vsVarianceShowSignPrefix,
    );
    const useTone = resolveRowUseToneColor(row, slot, vsVarianceUseToneColor);
    const useVarianceValueFormat =
      slot === 'vsVariance' || row.showSignPrefix === true;
    const signedArg = slot === 'vsVariance' ? showSign : true;
    const valueText = useVarianceValueFormat
      ? formatVarianceRowValue(row as ITechlandYtdVarianceRow, signedArg)
      : formatRowValue(row);
    const tone =
      slot === 'completion'
        ? resolveCompletionPercentTone(row.value)
        : resolveVarianceTone(row as ITechlandYtdVarianceRow, useTone);
    return (
      <div className="dbh-ytd-budget-plan-card-row">
        <div className="dbh-ytd-budget-plan-card-row-label">
          {(() => {
            const mappedLabel = row.labelByDateType?.[titleDateTypeKey];
            if (mappedLabel?.en) {
              return getLanguage(isEn, mappedLabel.en, mappedLabel.cn ?? mappedLabel.en);
            }
            return getLanguage(isEn, row.labelEn, row.labelCn);
          })()}
        </div>
        <div
          className={classnames(
            'dbh-ytd-budget-plan-card-row-value',
            `dbh-ytd-budget-plan-card-row-value--${tone}`,
          )}
        >
          {valueText}
        </div>
      </div>
    );
  };

  const renderGroup = (group: TMetricGroup): React.ReactNode => (
    <div className="dbh-ytd-budget-plan-card-group">
      {renderMetricRow(group.ytd, 'ytd')}
      {renderMetricRow(group.completion, 'completion')}
      {renderMetricRow(group.vsVariance, 'vsVariance')}
    </div>
  );

  const showBudgetGroup = !budgetGroup.hidden;
  const showPlanGroup = !planGroup.hidden;

  return (
    <Card
      className={classnames('dbh-ytd-budget-plan-card', className)}
      style={style}
    >
      <Card.Body
        title={
          <div className="dbh-ytd-budget-plan-card-title-container">
            {titleIconResolved ? (
              <img
                className="dbh-ytd-budget-plan-card-title-icon"
                src={titleIconResolved}
                alt=""
                aria-hidden
              />
            ) : null}
            <div className="dbh-ytd-budget-plan-card-title-main">
              <div>
                {getLanguage(isEn, displayTitleEn, displayTitleCn)}
              </div>
              {subtitleText ? (
                <div className="dbh-ytd-budget-plan-card-subtitle">
                  {subtitleText}
                </div>
              ) : null}
            </div>
          </div>
        }
      >
        <div className="dbh-ytd-budget-plan-card-body">
          <div className="dbh-ytd-budget-plan-card-actual">
            <div className="dbh-ytd-budget-plan-card-actual-label">
              {actualLabelResolved}
            </div>
            <div
              className="dbh-ytd-budget-plan-card-actual-value"
              style={actualValueStyle}
            >
              {actualDisplay}
            </div>
          </div>
          {showBudgetGroup ? (
            <>
              <div className="dbh-ytd-budget-plan-card-divider" aria-hidden />
              {renderGroup(budgetGroup)}
            </>
          ) : null}
          {showPlanGroup ? (
            <>
              <div className="dbh-ytd-budget-plan-card-divider" aria-hidden />
              {renderGroup(planGroup)}
            </>
          ) : null}
        </div>
      </Card.Body>
    </Card>
  );
};

export type {
  ITechlandYtdMetricRow,
  ITechlandYtdRevenueCardProps,
  ITechlandYtdVarianceRow,
  TTechlandYtdComputedValueKey,
} from './types';

export default TechlandYtdRevenueCard;
